import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Archive,
  BadgeDollarSign,
  Ban,
  FileDown,
  ShoppingCart,
  Tag,
  Tags,
  Truck,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type BulkAction,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { BulkOrderAction, OrderSummary } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { ImportExportLinks } from '@/features/imports-exports';
import { useStore } from '@/shared/store/store-context';

import { BulkOrdersDialog } from '../components/bulk-orders-dialog';
import { useOrderColumns } from '../components/order-columns';
import { orderFilters } from '../components/order-filters';
import { useExportOrders } from '../hooks/use-order-mutations';
import { useOrders } from '../hooks/use-orders';
import { BULK_MAX, BULK_PERMISSION } from '../schemas/bulk-schema';
import { exportFiltersFromParams, hasNegatedFilters } from '../schemas/order-export';

const ROUTE_ID = '/_authed/_active/s/$storeId/orders/' as const;
const ROUTE_PATH = '/s/$storeId/orders/' as const;

interface BulkRun {
  action: BulkOrderAction;
  orders: Array<{ id: string; name: string }>;
}

function Orders() {
  const { t } = useTranslation('orders');
  const store = useStore();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const message = useErrorMessage();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const exportOrders = useExportOrders(store.id);
  const [bulkRun, setBulkRun] = useState<BulkRun | null>(null);

  const filters = useMemo(
    () => orderFilters({ t, queryClient, storeId: store.id, currency: store.defaultCurrency }),
    [queryClient, store.defaultCurrency, store.id, t],
  );
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useOrders(store.id, list.params);
  const columns = useOrderColumns(store.id);
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'orders',
  });

  const startExport = useCallback(
    (ids?: string[]) => {
      exportOrders.mutate(ids ? { ids } : exportFiltersFromParams(list.params), {
        onSuccess: () =>
          toast.success(t('export.started'), {
            description:
              !ids && hasNegatedFilters(list.params) ? t('export.negatedNote') : undefined,
            action: {
              label: t('export.view'),
              onClick: () =>
                void navigate({
                  to: '/s/$storeId/settings/imports-exports',
                  params: { storeId: store.id },
                  search: { tab: 'exports' },
                }),
            },
          }),
        onError: (error) => toast.error(message(error)),
      });
    },
    [exportOrders, list.params, message, navigate, store.id, t],
  );

  const bulkActions = useMemo<Array<BulkAction<OrderSummary>>>(() => {
    const action = (
      id: BulkOrderAction,
      icon: React.ReactNode,
      extra: Partial<BulkAction<OrderSummary>> = {},
    ): BulkAction<OrderSummary> | null =>
      can(BULK_PERMISSION[id])
        ? {
            id,
            label: t(`bulk.${id}.action`),
            icon,
            onAction: (_selection, rows) => {
              if (rows.length === 0) return;
              if (rows.length > BULK_MAX) {
                toast.error(t('bulk.tooMany', { max: BULK_MAX }));
                return;
              }
              setBulkRun({ action: id, orders: rows.map((r) => ({ id: r.id, name: r.name })) });
            },
            ...extra,
          }
        : null;
    const actions = [
      action('add_tags', <Tag aria-hidden />, { promoted: true }),
      action('remove_tags', <Tags aria-hidden />),
      action('mark_paid', <BadgeDollarSign aria-hidden />),
      action('fulfil', <Truck aria-hidden />, { promoted: true }),
      action('archive', <Archive aria-hidden />),
      action('cancel', <Ban aria-hidden />, { variant: 'destructive' }),
    ].filter((a): a is BulkAction<OrderSummary> => a !== null);
    if (can('exports.run')) {
      actions.push({
        id: 'export',
        label: t('export.selected'),
        icon: <FileDown aria-hidden />,
        supportsAllMatching: true,
        keepSelection: true,
        onAction: (selection) => {
          if (selection.type === 'include') startExport(selection.ids);
          else startExport();
        },
      });
    }
    return actions;
  }, [can, startExport, t]);

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && query.data.items.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        actions={
          <>
            {can('exports.run') && narrowed ? (
              <Button
                variant="outline"
                disabled={exportOrders.isPending}
                onClick={() => startExport()}
              >
                <FileDown aria-hidden />
                {t('export.filtered')}
              </Button>
            ) : null}
            <div data-tour="orders.list.import-export" className="flex items-center gap-2">
              <ImportExportLinks storeId={store.id} kind="orders_csv" />
            </div>
          </>
        }
      />
      {isEmptyList ? (
        <EmptyState
          icon={ShoppingCart}
          title={t('empty.title')}
          description={t('empty.description')}
          tour="orders.list.empty"
          tourLink
        />
      ) : (
        <DataTable
          table={table}
          search={{ placeholder: t('search'), label: t('search') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          onRowClick={(row) =>
            void navigate({
              to: '/s/$storeId/orders/$orderId',
              params: { storeId: store.id, orderId: row.id },
            })
          }
          getRowLabel={(row) => row.name}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
          bulkActions={bulkActions}
          tour={{
            table: 'orders.list.table',
            search: 'orders.list.search',
            filters: 'orders.list.filters',
          }}
        />
      )}
      {bulkRun ? (
        <BulkOrdersDialog
          storeId={store.id}
          action={bulkRun.action}
          orders={bulkRun.orders}
          canRefund={can('orders.refund')}
          onOpenChange={(open) => {
            if (!open) setBulkRun(null);
          }}
        />
      ) : null}
    </PageContent>
  );
}

export function OrdersPage() {
  return (
    <FeatureErrorBoundary name="orders.list">
      <Orders />
    </FeatureErrorBoundary>
  );
}
